/**
 * 极简哈希路由。
 *
 * 为什么不用 react-router：
 *   只有 4 个平级页面，没有嵌套、没有权限路由、不需要 SSR。
 *   引一个路由库换来的是多一个依赖和一堆要读的文档，而手写只要 30 行。
 *
 * 用 hash 而不是 history：加到主屏后仍能靠返回手势工作，
 * 也省掉服务端配 fallback 的麻烦。
 */

import { useCallback, useEffect, useState } from 'react';

export const ROUTE_KEYS = ['fridge', 'cook', 'buy', 'settings'] as const;
export type RouteKey = (typeof ROUTE_KEYS)[number];

export interface RouteMeta {
	key: RouteKey;
	label: string;
}

export const ROUTES: RouteMeta[] = [
	{ key: 'fridge', label: '冰箱' },
	{ key: 'cook', label: '做菜' },
	{ key: 'buy', label: '待买' },
	{ key: 'settings', label: '设置' },
];

/** 顶部紧缩标题用的文案，比标签栏的长 */
export const ROUTE_TITLES: Record<RouteKey, string> = {
	fridge: '冰箱',
	cook: '做菜',
	buy: '待买',
	settings: '设置',
};

function parseHash(): RouteKey {
	const raw = window.location.hash.replace(/^#\/?/, '');
	return (ROUTE_KEYS as readonly string[]).includes(raw) ? (raw as RouteKey) : 'fridge';
}

export function useRoute(): [RouteKey, (next: RouteKey) => void] {
	const [route, setRoute] = useState<RouteKey>(() =>
		typeof window === 'undefined' ? 'fridge' : parseHash(),
	);

	useEffect(() => {
		const onChange = (): void => setRoute(parseHash());
		window.addEventListener('hashchange', onChange);
		return () => window.removeEventListener('hashchange', onChange);
	}, []);

	const navigate = useCallback((next: RouteKey) => {
		if (parseHash() === next) return;
		window.location.hash = `#/${next}`;
		// hashchange 在部分内嵌浏览器里不触发，这里补一手
		setRoute(next);
	}, []);

	return [route, navigate];
}
